<template>
  <div :class="props.height ? 'h-full flex flex-col' : ''" :style="props.height ? { height: props.height } : {}">
    <CalendarToolbar
      :date-iso="localIso(weekStart)"
      :date-label-long="weekLabel"
      :date-label-short="weekLabelShort"
      :hide-tab-switcher="hideTabSwitcher"
      :locked-teacher="lockedTeacher"
      :all-teachers="allTeachers"
      :all-instruments="allInstruments"
      :all-locations="allLocations"
      :active-teachers="activeTeachers"
      :selected-location="selectedLocation"
      :is-dark="isDark"
      :is-instrument-active="isInstrumentActive"
      :is-instrument-partial="isInstrumentPartial"
      :is-loading="isLoading"
      :readonly="readonly"
      @prev="prevWeek"
      @next="nextWeek"
      @jump-date="jumpToDate"
      @today="goToday"
      @refresh="fetchData"
      @new-lesson="openCreateModal({ date: localIso(weekStart), teacher: lockedTeacher, time: '' })"
      @toggle-teacher="toggleTeacher"
      @deselect-all="deselectAll"
      @toggle-instrument="toggleInstrument"
      @select-location="selectLocation"
    >
      <template #extra-controls>
        <slot name="extra-controls" />
      </template>
    </CalendarToolbar>

    <ScheduleNoticeBar v-if="!readonly" />

    <!-- Loading -->
    <div v-if="isLoading && !lessons.length" class="flex items-center justify-center py-20">
      <svg class="animate-spin h-6 w-6 text-fg-faint" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>

    <!-- Time grid: one column per day, lessons split side-by-side only when they overlap.
         Radius/clip live on this outer, non-scrolling wrapper — Chrome doesn't reliably clip
         `position: sticky` children to a border-radius on the element that also scrolls, so the
         scrollable div (ref="scrollContainer") stays plain and unrounded inside it. When a
         `height` prop is given, this wrapper fills the flex-column root exactly (flex-1 min-h-0)
         instead of taking an explicit height itself — a percentage height here would need the
         root to have a definite height to resolve against, and re-deriving that in two places
         is exactly the kind of drift that clipped the rounded corner before. -->
    <div v-else class="rounded-lg border border-stroke dark:border-stroke-dark overflow-hidden bg-calendar-bg dark:bg-calendar-bg-dark" :class="props.height ? 'flex-1 min-h-0' : ''">
    <div ref="scrollContainer" class="h-full overflow-auto" :style="props.height ? {} : { maxHeight: props.maxHeight }">
      <div style="min-width: 100%">

        <!-- Sticky header -->
        <div class="sticky top-0 z-30 border-b border-stroke dark:border-stroke-dark"
             :style="{ display: 'grid', gridTemplateColumns: gridCols }">

          <!-- Time gutter placeholder — drag to resize row height -->
          <div class="group sticky left-0 z-40 flex items-end justify-center cursor-ns-resize select-none bg-surface-raised dark:bg-surface-raised-dark border-r border-stroke dark:border-stroke-dark"
               :style="{ gridRow: 1, gridColumn: 1, width: TIME_COL_WIDTH + 'px', touchAction: 'none' }"
               title="Drag to resize rows"
               @pointerdown="startHourHeightResize">
            <ArrowsUpDownIcon class="w-4 h-4 mb-1 text-fg-muted dark:text-fg-muted-dark group-hover:text-blue-500 transition-colors" />
          </div>

          <!-- Day headers -->
          <div
            v-for="(day, di) in weekDays" :key="'dh-' + day.iso"
            class="flex items-baseline justify-center gap-1 sm:block text-center px-1.5 py-1.5 sm:py-2 overflow-hidden bg-surface-raised dark:bg-surface-raised-dark"
            :class="[
              di < weekDays.length - 1 ? 'border-r border-stroke dark:border-stroke-dark' : ''
            ]"
            :style="{ gridRow: 1, gridColumnStart: di + 2 }"
          >
            <div class="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-fg-muted dark:text-fg-muted-dark">{{ day.weekday }}</div>
            <div class="flex justify-center">
              <span :class="['inline-flex items-center justify-center rounded-full font-bold text-base sm:text-xl leading-none transition-colors',
                day.isToday ? 'bg-blue-600 text-white w-7 h-7 sm:w-9 sm:h-9' : 'text-fg-base dark:text-fg-base-dark']">{{ day.dayNum }}</span>
            </div>
          </div>
        </div>

        <!-- Body grid -->
        <div :style="{ display: 'grid', gridTemplateColumns: gridCols, height: bodyHeight + 'px' }">

          <!-- Time gutter -->
          <div class="sticky left-0 z-10 bg-surface dark:bg-surface-raised-dark border-r border-stroke dark:border-stroke-dark relative"
               :style="{ width: TIME_COL_WIDTH + 'px', height: bodyHeight + 'px' }">
            <div v-for="h in displayHours" :key="h"
                 class="absolute right-2 text-[10px] text-fg-faint dark:text-fg-faint-dark leading-none"
                 :style="{ top: (hourToY(h, computedMinHour) - 5) + 'px' }">
              {{ fmtHour(h) }}
            </div>
          </div>

          <!-- One column per day — lessons from every active teacher, colored/labeled by teacher -->
          <DayTimeGrid
            v-for="(day, di) in weekDays" :key="day.iso"
            :day="day"
            :col="{ key: day.iso, teacher: lockedTeacher, availability: null }"
            :lessons="lessonsForDay(day.iso)"
            :time-off-blocks="timeOffBlocksForDay(day.iso)"
            :min-hour="computedMinHour"
            :max-hour="computedMaxHour"
            :is-dark="isDark"
            :color-by-teacher="true"
            :show-availability="false"
            :readonly="readonly"
            @create-lesson="openCreateModal"
            @mark-time-off="openTimeOffModal"
            @lessons-removed="removeLessons"
            @time-off-deleted="removeCancellation"
            class="border-r border-stroke dark:border-stroke-dark"
            :class="di < weekDays.length - 1 ? 'border-r-2 border-r-stroke-strong dark:border-r-stroke-strong-dark' : ''"
            :style="{ gridColumnStart: di + 2 }"
          />
        </div>

      </div>
    </div>
    </div>
  </div>

  <LessonCreateModal
    v-if="createProps"
    v-bind="createProps"
    @close="createProps = null"
    @saved="() => { createProps = null; fetchData() }"
  />

  <TeacherCancellationModal
    v-if="showTimeOffModal"
    :initial-date="timeOffDate"
    :initial-time="timeOffTime"
    :teacher-name="timeOffTeacher"
    @close="showTimeOffModal = false; fetchData()"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { ArrowsUpDownIcon } from '@heroicons/vue/24/outline'
import {
  useCalendarData,
  hourHeight, TIME_COL_WIDTH, startHourHeightResize,
  hourToY, parseLessonTime, localIso, getMonday,
  fmtHour, fitHourHeightToContainer,
} from '@/composables/useCalendarData.js'
import { useCalendarFilters } from '@/composables/useCalendarFilters.js'
import { useNow } from '@/composables/useNow.js'
import DayTimeGrid from '@/components/calendar/DayTimeGrid.vue'
import ScheduleNoticeBar from '@/components/calendar/ScheduleNoticeBar.vue'
import CalendarToolbar from '@/components/calendar/CalendarToolbar.vue'
import LessonCreateModal from '@/components/common/LessonCreateModal.vue'
import TeacherCancellationModal from '@/components/common/TeacherCancellationModal.vue'
import { useDemoMode } from '@/composables/useDemoMode'
import { useAdminSidebar } from '@/composables/useAdminSidebar.js'

const DAY_COL_MIN_WIDTH = 150

const props = defineProps({
  lessonEndpoint: { type: String, default: '/api/admin/calendar' },
  lockedTeacher:  { type: String, default: null },  // when set: single teacher, no filter bar
  maxHeight:      { type: String, default: 'min(90vh, 2000px)' },  // CSS length — caps the box, but it can render shorter if content is shorter
  height:         { type: String, default: null },    // CSS length — forces the box to exactly this size regardless of content (for resizable panels)
  hideTabSwitcher: { type: Boolean, default: false },  // suppress the router-based Day/Week/Month select (parent supplies its own)
  readonly:        { type: Boolean, default: false },  // suppress click-to-create-lesson (read-only embed)
})

const currentUser = JSON.parse(localStorage.getItem('user') || 'null')
const effectivePermissions = useDemoMode().effectivePermissionsFor(() => currentUser?.permissions)
const isAdmin = computed(() => effectivePermissions.value.includes('admin'))
const hasSignupsPermission = computed(() => effectivePermissions.value.includes('admin') || effectivePermissions.value.includes('lesson_signups'))

// Admin pages open the form in the admin sidebar; the teacher dashboard's embed as a popup.
const adminSidebar = useAdminSidebar()
const createProps = ref(null)
const openCreateModal = ({ date, teacher, time }) => {
  const p = {
    initialDate:          date,
    initialTime:          time || '',
    lockedTeacherName:    hasSignupsPermission.value ? null : (props.lockedTeacher || currentUser?.name),
    lockedTeacherId:      hasSignupsPermission.value ? null : currentUser?.id,
    canManageSignups:     hasSignupsPermission.value,
    preselectTeacherName: teacher || '',
  }
  if (adminSidebar.available.value) adminSidebar.open('lesson-create', p, { onSaved: fetchData })
  else createProps.value = p
}

const showTimeOffModal = ref(false)
const timeOffDate = ref('')
const timeOffTime = ref('')
const timeOffTeacher = ref('')
const openTimeOffModal = ({ date, teacher, time }) => {
  timeOffDate.value = date
  timeOffTime.value = time || ''
  timeOffTeacher.value = teacher || ''
  showTimeOffModal.value = true
}

// ── Dark mode ─────────────────────────────────────────────────────────────────
const isDark = ref(document.documentElement.classList.contains('dark'))
let _darkObserver
onMounted(() => {
  _darkObserver = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark')
  })
  _darkObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})
onUnmounted(() => _darkObserver?.disconnect())

// ── Calendar data ─────────────────────────────────────────────────────────────
const {
  lessons, isLoading,
  weekStart, weekDays, weekLabel,
  prevWeek, nextWeek, goToday,
  teacherMap, allTeachers, allInstruments, allLocations,
  timeOffBlocksForDay,
  fetchData, removeLessons, removeCancellation,
} = useCalendarData({ lessonEndpoint: props.lessonEndpoint, fetchTeachers: !props.lockedTeacher })

const jumpToDate = iso => { weekStart.value = getMonday(new Date(iso + 'T00:00:00')) }

// Narrow-toolbar fallback for weekLabel — see CalendarToolbar's dv-date-long/dv-date-short.
const weekLabelShort = computed(() => {
  const s = weekStart.value
  const e = new Date(s); e.setDate(e.getDate() + 6)
  return `${s.getMonth() + 1}/${s.getDate()}–${e.getMonth() + 1}/${e.getDate()}/${String(e.getFullYear()).slice(-2)}`
})

// Vertical scroll: with a fixed 0–24h axis the grid is usually taller than the box,
// so land on "now" (current week) or the week's earliest lesson (other weeks) instead of hour 0.
const scrollContainer = ref(null)
const { nowHourFraction } = useNow()
const scrollToRelevantTime = () => {
  if (!scrollContainer.value) return
  let targetHour = 8
  if (weekDays.value.some(d => d.isToday)) {
    targetHour = nowHourFraction.value
  } else {
    let earliest = null
    for (const day of weekDays.value)
      for (const l of lessonsForDay(day.iso)) {
        const p = parseLessonTime(l.lesson_time)
        if (p) earliest = earliest === null ? p.h : Math.min(earliest, p.h)
      }
    if (earliest !== null) targetHour = earliest
  }
  scrollContainer.value.scrollTop = Math.max(0, (targetHour - 1) * hourHeight.value)
}

// Zoom-anchor fix: dragging the row-height handle changes hourHeight, which rescales
// every y-position from the top of the grid. Without this, whatever's on screen drifts
// as you drag. Keep the hour at viewport-center stationary instead. (flush: 'post' so
// the DOM has already resized to the new bodyHeight before we touch scrollTop.)
watch(hourHeight, (newH, oldH) => {
  const el = scrollContainer.value
  if (!el || !oldH) return
  const viewportH = el.clientHeight
  el.scrollTop = (el.scrollTop + viewportH / 2) * (newH / oldH) - viewportH / 2
}, { flush: 'post' })

watch(() => weekDays.value[0]?.iso, async () => {
  lessons.value = []
  await fetchData()
  await nextTick()
  fitHourHeightToContainer(scrollContainer.value, computedMaxHour.value - computedMinHour.value)
  scrollToRelevantTime()
}, { immediate: true })

// ── Filters ───────────────────────────────────────────────────────────────────
const {
  activeTeachers, selectedLocation,
  toggleTeacher, deselectAll,
  toggleInstrument, isInstrumentActive, isInstrumentPartial,
  selectLocation, matchesLocation,
  filteredTeachers: filteredTeachersRaw,
} = useCalendarFilters({ allTeachers, teacherMap, shared: !props.lockedTeacher })

const filteredTeachers = computed(() => props.lockedTeacher ? [props.lockedTeacher] : filteredTeachersRaw.value)

// All lessons on a given date from currently-active teachers, matching the location filter
const lessonsForDay = iso =>
  lessons.value
    .filter(l => l.lesson_date === iso && filteredTeachers.value.includes(l.teacher_name) && matchesLocation(l))
    .sort((a, b) => (a.lesson_time || '').localeCompare(b.lesson_time || ''))

// ── Dynamic Y-axis ────────────────────────────────────────────────────────────
const hourRange = computed(() => {
  let min = 23, max = 0
  for (const day of weekDays.value)
    for (const l of lessonsForDay(day.iso)) {
      const p = parseLessonTime(l.lesson_time)
      if (p) {
        min = Math.min(min, Math.max(0, p.h - 1))
        max = Math.max(max, Math.min(24, Math.ceil((p.h * 60 + p.m + (l.duration_minutes || 30)) / 60) + 1))
      }
    }
  const lo = min === 23 ? 8 : min
  const hi = max === 0 ? lo + 5 : Math.max(max, lo + 5)
  return { min: lo, max: hi }
})
const computedMinHour = computed(() => hourRange.value.min)
const computedMaxHour = computed(() => hourRange.value.max)

const bodyHeight   = computed(() => (computedMaxHour.value - computedMinHour.value) * hourHeight.value)
const displayHours = computed(() =>
  Array.from({ length: computedMaxHour.value - computedMinHour.value }, (_, i) => computedMinHour.value + i)
)

const gridCols = computed(() =>
  [`${TIME_COL_WIDTH}px`, ...Array(7).fill(`minmax(${DAY_COL_MIN_WIDTH}px, 1fr)`)].join(' ')
)
</script>
